// useMessages — learner-side live subscription to /messages.
//
// A learner sees a message if either:
//   1. broadcast == true (everyone)
//   2. audienceUserIds contains this learner's userId
//
// Firestore can't OR these in one query, so we run two parallel
// queries and merge client-side by docId. Retracted rows are filtered
// here too so the listener removes the row instantly when the worker
// flips the flag.
//
// Sort: createdAt DESC, tie-break by id.

import {
  type MessageDoc,
  type WithId,
  messagesCol,
  useFirestoreCollection,
} from "@goodbye/firebase";
import { Timestamp, orderBy, query, where } from "firebase/firestore";
import { useMemo } from "react";

export interface MessagesState {
  messages: WithId<MessageDoc>[];
  loading: boolean;
  error: Error | null;
}

function tsMs(t: Timestamp | null | undefined): number {
  if (!t || typeof t.toMillis !== "function") return 0;
  return t.toMillis();
}

export function useMessages(learnerUserId: string | null): MessagesState {
  const broadcastQ = useMemo(() => {
    if (!learnerUserId) return null;
    return query(
      messagesCol(),
      where("broadcast", "==", true),
      orderBy("createdAt", "desc"),
    );
  }, [learnerUserId]);

  const targetedQ = useMemo(() => {
    if (!learnerUserId) return null;
    return query(
      messagesCol(),
      where("audienceUserIds", "array-contains", learnerUserId),
      orderBy("createdAt", "desc"),
    );
  }, [learnerUserId]);

  const broadcast = useFirestoreCollection(broadcastQ);
  const targeted = useFirestoreCollection(targetedQ);

  const merged = useMemo(() => {
    const byId = new Map<string, WithId<MessageDoc>>();
    for (const m of broadcast.data) byId.set(m.id, m);
    for (const m of targeted.data) byId.set(m.id, m);
    const list = Array.from(byId.values()).filter((m) => m.retracted !== true);
    list.sort((a, b) => {
      const diff = tsMs(b.createdAt) - tsMs(a.createdAt);
      if (diff !== 0) return diff;
      return a.id.localeCompare(b.id);
    });
    return list;
  }, [broadcast.data, targeted.data]);

  if (!learnerUserId) {
    return { messages: [], loading: false, error: null };
  }
  return {
    messages: merged,
    loading: broadcast.loading || targeted.loading,
    error: broadcast.error ?? targeted.error,
  };
}
